import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Loader2, Phone, RotateCcw, Trash2, IndianRupee } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { fmtTime, fmtDate } from "@/lib/slots";

function RefundBox({ b, onSaved }) {
  const r = b.refund || {};
  const [amount, setAmount] = useState(String(r.amount ?? b.advance_paid ?? ""));
  const [reason, setReason] = useState(r.reason || "");
  const [status, setStatus] = useState(r.status || "pending");
  const save = async () => {
    try { await api.post(`/admin/bookings/${b.id}/refund`, { amount: Number(amount) || 0, reason, status }); toast.success("Refund saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-purple-500/10">
      <p className="text-[11px] uppercase tracking-wider text-purple-400 font-mono mb-2 flex items-center gap-1.5"><IndianRupee size={13} /> Advance Refund</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Input data-testid={`refund-amount-${b.booking_id}`} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount ₹" className="h-9 w-24 bg-purple-950/30 border-purple-500/30" />
        <Input data-testid={`refund-reason-${b.booking_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="h-9 flex-1 min-w-[140px] bg-purple-950/30 border-purple-500/30" />
        <select data-testid={`refund-status-${b.booking_id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-xl bg-purple-950/40 border border-purple-500/30 px-2 text-sm">
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
        </select>
        <Button size="sm" data-testid={`refund-save-${b.booking_id}`} onClick={save} className="h-9 bg-emerald-600 hover:bg-emerald-500">Save</Button>
      </div>
      {b.refund && <p className="text-[11px] text-slate-400 mt-1.5">Recorded: ₹{b.refund.amount} · {b.refund.status}{b.refund.reason ? ` · ${b.refund.reason}` : ""}</p>}
    </div>
  );
}

export default function CancelledTab() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/bookings", { params: { filter: "cancelled", search, archived: showArchived ? "true" : "" } }).then((r) => setItems(r.data));
  }, [search, showArchived]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const restore = async (id) => {
    try { await api.put(`/admin/bookings/${id}`, { status: "confirmed" }); toast.success("Booking restored"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cleanup = async () => {
    if (!window.confirm("Archive all bookings cancelled 30+ days ago? (financial data is kept, just moved to archive)")) return;
    try { const r = await api.post("/admin/bookings/cancelled-cleanup", null, { params: { days: 30 } }); toast.success(`Archived ${r.data.archived} old cancelled booking(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input data-testid="cancelled-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone or booking ID" className="pl-9 h-11 bg-purple-950/30 border-purple-500/30" />
        </div>
        <Button variant="outline" data-testid="toggle-archived-btn" onClick={() => setShowArchived((v) => !v)} className={`h-11 border-purple-500/30 bg-transparent ${showArchived ? "text-purple-200" : "text-slate-300"}`}>{showArchived ? "Show Active" : "Show Archived"}</Button>
        <Button variant="outline" data-testid="cleanup-cancelled-btn" onClick={cleanup} className="h-11 border-amber-500/30 bg-transparent text-amber-300"><Trash2 size={15} /> Archive 30+ Days Old</Button>
      </div>

      {!items ? <Loader2 className="animate-spin text-purple-400" /> : items.length === 0 ? (
        <p className="text-slate-500 text-center py-10" data-testid="cancelled-empty">No cancelled bookings.</p>
      ) : (
        <div className="space-y-3" data-testid="cancelled-list">
          {items.map((b) => (
            <div key={b.id} className="glass rounded-2xl p-4" data-testid={`cancelled-row-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-purple-300 text-sm">{b.booking_id}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full border uppercase text-red-400 bg-red-500/10 border-red-500/30">cancelled</span>
                  </div>
                  <p className="font-semibold mt-1">{b.customer_name} · <a href={`tel:${b.mobile}`} className="text-slate-400 font-normal"><Phone size={12} className="inline" /> {b.mobile}</a></p>
                  <p className="text-sm text-slate-400">{fmtDate(b.date)} · {b.station_name}</p>
                  <p className="text-sm text-slate-400">{b.setup_name} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)} · {b.duration_label}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg font-bold text-slate-300">₹{b.amount}</p>
                  <p className="text-xs text-slate-500 uppercase">{b.payment_method}{b.source === "walkin" ? " · walk-in" : ""}</p>
                  {(b.advance_paid || 0) > 0 && <p className="text-[11px] text-emerald-300 mt-1">Advance paid ₹{b.advance_paid}</p>}
                </div>
              </div>
              <RefundBox b={b} onSaved={load} />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => restore(b.id)} data-testid={`restore-${b.booking_id}`} className="h-8 border-emerald-500/30 bg-transparent text-emerald-300"><RotateCcw size={13} /> Restore Booking</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
